import { useState, useCallback, useMemo } from 'react'
import { useLocalStorage } from 'react-use'
import { useNotificationSlice } from 'redux/slices/hooks'

interface RateLimitConfig {
  /**
   * Unique key for localStorage
   */
  key: string
  /**
   * Maximum number of downloads allowed within the time frame
   */
  maxDownloads: number
  /**
   * Time frame in minutes
   */
  timeFrameMinutes: number
}

interface RateLimitState {
  count: number
  startedAt: number
}

interface UseRateLimiterReturn {
  /**
   * Check if download is allowed and execute the callback if permitted
   * Returns true if download was executed, false if rate limited
   */
  checkAndExecute: (callback: () => Promise<void> | void) => Promise<boolean>
  /**
   * Check if rate limit is currently exceeded
   */
  isRateLimited: () => boolean
  /**
   * Get remaining downloads in current time window
   */
  getRemainingDownloads: () => number
  /**
   * Reset the rate limit counter
   */
  reset: () => void
}

/**
 * Centralized rate limiting hook for downloads
 *
 * Prevents spam by limiting the number of downloads within a time window.
 * Uses localStorage to persist state across sessions.
 *
 * @example
 * ```ts
 * const rateLimiter = useRateLimiter({
 *   key: 'cell-images-download',
 *   maxDownloads: 10,
 *   timeFrameMinutes: 10,
 * })
 *
 * const handleDownload = async () => {
 *   const allowed = await rateLimiter.checkAndExecute(async () => {
 *     // Download logic here
 *   })
 *
 *   if (!allowed) {
 *     console.log('Rate limited')
 *   }
 * }
 * ```
 */
const useRateLimiter = (config: RateLimitConfig): UseRateLimiterReturn => {
  const { key, maxDownloads, timeFrameMinutes } = config
  const { showError } = useNotificationSlice()

  const [rateLimitState, setRateLimitState] = useLocalStorage<RateLimitState>(key, {
    count: 0,
    startedAt: 0,
  })

  const [timeLimit, setTimeLimit] = useState(0)

  /**
   * Check if rate limit is currently exceeded
   */
  const isRateLimited = useCallback((): boolean => {
    if (!rateLimitState) return false

    const now = Date.now()
    const timeWindowExpired = rateLimitState.startedAt > 0 && now > timeLimit

    // If time window expired, rate limit is no longer active
    if (timeWindowExpired) {
      return false
    }

    // Check if count exceeded
    return rateLimitState.count >= maxDownloads && rateLimitState.startedAt < timeLimit
  }, [rateLimitState, timeLimit, maxDownloads])

  /**
   * Get remaining downloads in current time window
   */
  const getRemainingDownloads = useCallback((): number => {
    if (!rateLimitState) return maxDownloads

    const now = Date.now()
    const timeWindowExpired = rateLimitState.startedAt > 0 && now > timeLimit

    if (timeWindowExpired) {
      return maxDownloads
    }

    return Math.max(0, maxDownloads - rateLimitState.count)
  }, [rateLimitState, timeLimit, maxDownloads])

  /**
   * Reset the rate limit counter
   */
  const reset = useCallback(() => {
    setRateLimitState({
      count: 0,
      startedAt: 0,
    })
    setTimeLimit(0)
  }, [setRateLimitState])

  /**
   * Check if download is allowed and execute callback
   */
  const checkAndExecute = useCallback(
    async (callback: () => Promise<void> | void): Promise<boolean> => {
      if (!rateLimitState) {
        return false
      }

      const now = Date.now()
      const timeWindowExpired = rateLimitState.startedAt > 0 && now > timeLimit
      const isFirstDownload = rateLimitState.count === 0
      const withinLimits =
        rateLimitState.count < maxDownloads && rateLimitState.startedAt < timeLimit

      // Handle different scenarios
      if (isFirstDownload || timeWindowExpired) {
        // Start new time window
        const newTimeLimit = now + timeFrameMinutes * 60 * 1000
        setTimeLimit(newTimeLimit)

        await callback()

        setRateLimitState({
          count: 1,
          startedAt: now,
        })

        return true
      }

      if (withinLimits) {
        // Within limits, increment counter
        await callback()

        setRateLimitState({
          count: rateLimitState.count + 1,
          startedAt: rateLimitState.startedAt,
        })

        return true
      }

      // Rate limited
      showError(
        'Error while downloading/archiving images: Maximum download exceeded',
        undefined,
        'error'
      )

      return false
    },
    [
      rateLimitState,
      timeLimit,
      maxDownloads,
      timeFrameMinutes,
      setRateLimitState,
      showError,
    ]
  )

  return useMemo(
    () => ({
      checkAndExecute,
      isRateLimited,
      getRemainingDownloads,
      reset,
    }),
    [checkAndExecute, isRateLimited, getRemainingDownloads, reset]
  )
}

export default useRateLimiter
